const QUEUE_KEY = "holi_attribution_queue";
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 10;

export interface Attribution extends UtmParams, GtmParams, ClickIds {
  gaClientId: string;
  firstTouchReferrer?: string;
  landingPage?: string;
  inApp?: InApp;
  utmCapturedAt?: string;
  lastSeenAt?: string;
}

export interface UtmParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

export interface GtmParams {
  gtmSource?: string;
  gtmCampaign?: string;
  gtmMedium?: string;
}

export interface ClickIds {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  fbAdId?: string;
  gadSource?: string;
  gadCampaignId?: string;
  utmId?: string;
}

export type InApp = "instagram" | "facebook" | "tiktok" | "linkedin" | "snapchat";

interface AttributionQueueEntry extends Attribution {
  expiresAt: string;
}

const CLICK_PARAMS: Record<keyof ClickIds, string> = {
  gclid: "gclid",
  gbraid: "gbraid",
  wbraid: "wbraid",
  fbclid: "fbclid",
  ttclid: "ttclid",
  msclkid: "msclkid",
  fbAdId: "fb_ad_id",
  gadSource: "gad_source",
  gadCampaignId: "gad_campaignid",
  utmId: "utm_id",
};

export function getGaClientId(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;
  const parts = match[1].split(".");
  if (parts.length < 4) return null;
  return parts.slice(2).join(".");
}

export function getUtmParams(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  return stripUndefined({
    utmSource: params.get("utm_source") ?? undefined,
    utmMedium: params.get("utm_medium") ?? undefined,
    utmCampaign: params.get("utm_campaign") ?? undefined,
    utmTerm: params.get("utm_term") ?? undefined,
    utmContent: params.get("utm_content") ?? undefined,
  });
}

export function getClickIds(): ClickIds {
  const params = new URLSearchParams(window.location.search);
  const clickIds: ClickIds = {};
  for (const [key, param] of Object.entries(CLICK_PARAMS) as [keyof ClickIds, string][]) {
    const value = params.get(param);
    if (value) clickIds[key] = value.slice(0, 500);
  }
  return clickIds;
}

export function getGtmParams(): GtmParams {
  const dataLayer: Array<Record<string, unknown>> = window.dataLayer ?? [];
  const gtm: GtmParams = {};

  for (const entry of dataLayer) {
    if (typeof entry.source === "string") gtm.gtmSource = entry.source;
    if (typeof entry.campaign === "string") gtm.gtmCampaign = entry.campaign;
    if (typeof entry.medium === "string") gtm.gtmMedium = entry.medium;
  }

  return stripUndefined(gtm);
}

export function getInApp(): InApp | undefined {
  const ua = navigator.userAgent || "";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return "facebook";
  if (/BytedanceWebview|musical_ly|TikTok/i.test(ua)) return "tiktok";
  if (/LinkedInApp/i.test(ua)) return "linkedin";
  if (/Snapchat/i.test(ua)) return "snapchat";
  return undefined;
}

function isExternalUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return !!host && host !== window.location.hostname && !/(^|\.)klinikaholi\.pl$/i.test(host);
  } catch {
    return false;
  }
}

export function getReferrer(): string | undefined {
  const holiRef = new URLSearchParams(window.location.search).get("holi_ref");
  if (holiRef && /^[a-z0-9.-]{3,253}$/i.test(holiRef)) return `https://${holiRef}`;

  const dataLayer: Array<Record<string, unknown>> = window.dataLayer ?? [];
  const keys = ["page_referrer", "referrer"] as const;
  for (const entry of dataLayer) {
    for (const key of keys) {
      const value = entry[key];
      if (typeof value === "string" && value && isExternalUrl(value)) {
        return value;
      }
    }
  }
  const docRef = document.referrer;
  if (docRef && isExternalUrl(docRef)) return docRef;
  return undefined;
}

function loadQueue(): AttributionQueueEntry[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: AttributionQueueEntry[]): boolean {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(limitQueue(queue)));
    return true;
  } catch {
    return false;
  }
}

function limitQueue(queue: AttributionQueueEntry[]): AttributionQueueEntry[] {
  if (queue.length <= MAX_ENTRIES) return queue;
  const firstTouch = oldestEntry(queue);
  const evicted = queue
    .filter((e) => e !== firstTouch)
    .sort((a, b) => lastSeenTime(a) - lastSeenTime(b))
    .slice(0, queue.length - MAX_ENTRIES);
  return queue.filter((e) => !evicted.includes(e));
}

function capturedTime(entry: AttributionQueueEntry): number {
  return entry.utmCapturedAt ? new Date(entry.utmCapturedAt).getTime() : 0;
}

function lastSeenTime(entry: AttributionQueueEntry): number {
  const seenAt = entry.lastSeenAt ?? entry.utmCapturedAt;
  return seenAt ? new Date(seenAt).getTime() : 0;
}

function oldestEntry(entries: AttributionQueueEntry[]): AttributionQueueEntry {
  return entries.reduce((a, b) => (capturedTime(a) <= capturedTime(b) ? a : b));
}

function isActive(entry: AttributionQueueEntry, now: number = Date.now()): boolean {
  return new Date(entry.expiresAt).getTime() > now;
}

function clickType(clickIds: ClickIds): string {
  if (clickIds.gclid || clickIds.gbraid || clickIds.wbraid || clickIds.gadCampaignId) return "google";
  if (clickIds.fbAdId) return "meta_ad";
  if (clickIds.ttclid) return "tiktok";
  if (clickIds.msclkid) return "bing";
  return "";
}

function hasSource(entry: AttributionQueueEntry): boolean {
  return !!(entry.utmSource || clickType(entry) || entry.fbclid || entry.firstTouchReferrer);
}

function entryMatches(entry: AttributionQueueEntry, ctx: CaptureContext): boolean {
  return (
    entry.utmSource === ctx.utm.utmSource &&
    entry.utmMedium === ctx.utm.utmMedium &&
    entry.utmCampaign === ctx.utm.utmCampaign &&
    clickType(entry) === clickType(ctx.clickIds) &&
    (entry.inApp ?? "") === (ctx.inApp ?? "")
  );
}

function stripUndefined<T extends object>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).filter(([, v]) => v !== undefined)
  ) as T;
}

interface CaptureContext {
  gaClientId: string;
  utm: UtmParams;
  gtm: GtmParams;
  clickIds: ClickIds;
  inApp: InApp | undefined;
  referrer: string | undefined;
  expiresAt: string;
}

function withoutClickIds(entry: AttributionQueueEntry): AttributionQueueEntry {
  return Object.fromEntries(Object.entries(entry).filter(([key]) => !(key in CLICK_PARAMS))) as AttributionQueueEntry;
}

function mergeIntoExisting(existing: AttributionQueueEntry, ctx: CaptureContext): AttributionQueueEntry {
  return stripUndefined({
    ...(Object.keys(ctx.clickIds).length > 0 ? withoutClickIds(existing) : existing),
    ...ctx.clickIds,
    gaClientId: ctx.gaClientId,
    utmTerm: ctx.utm.utmTerm ?? existing.utmTerm,
    utmContent: ctx.utm.utmContent ?? existing.utmContent,
    gtmSource: ctx.gtm.gtmSource ?? existing.gtmSource,
    gtmCampaign: ctx.gtm.gtmCampaign ?? existing.gtmCampaign,
    gtmMedium: ctx.gtm.gtmMedium ?? existing.gtmMedium,
    firstTouchReferrer: existing.firstTouchReferrer ?? ctx.referrer,
    inApp: existing.inApp ?? ctx.inApp,
    lastSeenAt: new Date().toISOString(),
    expiresAt: ctx.expiresAt,
  });
}

function createEntry(ctx: CaptureContext): AttributionQueueEntry {
  const now = new Date().toISOString();
  return stripUndefined({
    gaClientId: ctx.gaClientId,
    ...ctx.utm,
    ...ctx.gtm,
    ...ctx.clickIds,
    firstTouchReferrer: ctx.referrer,
    landingPage: window.location.pathname + window.location.search,
    inApp: ctx.inApp,
    utmCapturedAt: now,
    lastSeenAt: now,
    expiresAt: ctx.expiresAt,
  });
}

export function captureAttribution(): boolean {
  const gaClientId = getGaClientId();
  if (!gaClientId) return false;

  const ctx: CaptureContext = {
    gaClientId,
    utm: getUtmParams(),
    gtm: getGtmParams(),
    clickIds: getClickIds(),
    inApp: getInApp(),
    referrer: getReferrer(),
    expiresAt: new Date(Date.now() + EXPIRY_MS).toISOString(),
  };

  const queue = loadQueue()
    .filter((e) => isActive(e))
    .map((e) => ({ ...e, gaClientId }));

  const existingIndex = queue.findIndex((e) => entryMatches(e, ctx));
  if (existingIndex >= 0) {
    queue[existingIndex] = mergeIntoExisting(queue[existingIndex], ctx);
  } else {
    queue.push(createEntry(ctx));
  }

  return saveQueue(queue);
}

export function getAttribution(): Attribution | null {
  const active = loadQueue().filter((e) => isActive(e));
  if (active.length === 0) return null;

  const oldest = oldestEntry(active);

  return stripUndefined({ ...oldest, expiresAt: undefined }) as Attribution;
}

export function getLastAttribution(): Attribution | null {
  const active = loadQueue().filter((e) => isActive(e));
  if (active.length === 0) return null;

  const withSource = active.filter(hasSource);
  const pool = withSource.length > 0 ? withSource : active;
  const newest = pool.reduce((a, b) => (lastSeenTime(a) >= lastSeenTime(b) ? a : b));

  return stripUndefined({ ...newest, expiresAt: undefined }) as Attribution;
}
